import { ReactElement } from 'react';
import * as stylex from '@stylexjs/stylex';
import { color as token, spacer } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { Dropdown } from '@/UI/Dropdown/Dropdown';
import { FormControl } from '@/UI/FormControl/FormControl';
import { Input, LineInput } from '@/UI/Input/Input';
import { AppCheckbox } from '@/UI/AppCheckbox/AppCheckbox';
import { Skeleton } from '@/UI/Skeleton/Skeleton';
import { TextArea } from '@/UI/TextArea/TextArea';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { DesignAppMode } from '@/enums/design-app-modes.enum';
import { NodeType } from '@/enums/node-type.enum';
import { getUpstreamNodes } from '@/components/Recipe/utils/edgeSelectionGeometry';
import { getToolInputLeaves } from '@/queries/toolConfigModel';
import { I18N_KEYS } from '@/language/keys';
import { Node, Edge, type BaseNodeData } from '@/types/node';
import { isIterator } from '@/utils/iterator.utils';
import { FlexCenVer } from '@/UI/styles';
import { NumberInputField } from '../../Nodes/input-fields/number-input-field/number-input-field.component';
import { TextFieldWithCursor } from '../../Nodes/ModelNodesUtils';
import UploadFile from './DesignAppUploadFile';
import DesignAppColorPaletteInput from './DesignAppColorPaletteInput';
import { LoraSelect } from './LoraSelect';
import { MultiLoraWeightField } from './MultiLoraWeightField';
import type { ToolConfig } from '@/types/tool-config.types';
import type { DesignAppInput } from '@/types/design-app.types';
import type { TFunction } from 'i18next';
import type { Color as WasmColor, ImageFile } from 'web';

interface ValidationProps {
  error?: boolean;
  helperText?: string | null;
}

type HandleChangeFunction = (paramId: string, newValue: any) => void;

interface SeedValue {
  isRandom: boolean;
  seed: string | number;
}

interface MultiLoraValue {
  selectedLora: {
    id: string;
    name: string;
    file?: string;
    coverImage?: ImageFile;
    defaultWeight: number;
    trigger: string;
    url?: string;
  } | null;
  weight: number;
}

interface PromptValue {
  prompt: string;
}

interface RenderDesignAppParamsProps {
  node?: any;
  param: any;
  handleChange: HandleChangeFunction;
  isLoading: boolean;
  validationProps: ValidationProps;
  translate: TFunction;
  disabled: boolean;
  isFplMigrationEnabled: boolean;
  listSelectorOptions?: (nodeId: string) => string[];
}

const styles = stylex.create({
  muxControl: {
    width: '100%',
    minWidth: 0,
    marginTop: spacer.spacer2,
  },
  booleanControl: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    marginTop: spacer.spacer2,
  },
  seedControl: {
    display: 'flex',
    alignItems: 'center',
    gap: spacer.spacer2,
    marginTop: spacer.spacer2,
  },
  seedInput: {
    width: '130px',
  },
});

const getSafeNumber = (value: any): number | undefined => {
  const parsedValue = Number.parseFloat(value);
  if (isNaN(parsedValue)) {
    return undefined;
  }
  return parsedValue;
};

export const renderDesignAppParams = ({
  node,
  param,
  handleChange,
  isLoading,
  validationProps,
  translate,
  disabled,
  isFplMigrationEnabled,
  listSelectorOptions,
}: RenderDesignAppParamsProps): ReactElement | null => {
  switch (param.type) {
    case 'integer':
    case 'number':
      return (
        <NumberInputField
          key={`${param.id}-numberInputField`}
          inputKey={param.id}
          title={node?.data?.name || ''}
          description=""
          // @ts-expect-error - fix this
          value={getSafeNumber(param.value)}
          // @ts-expect-error - fix this
          min={getSafeNumber(node?.data?.min)}
          // @ts-expect-error - fix this
          max={getSafeNumber(node?.data?.max)}
          type={param.mode || node?.type}
          onChange={handleChange}
          disabled={disabled}
        />
      );
    case 'input': // backward compatibility 24.10 (adding separation between input of integer and floats)
      return (
        <Input
          key={`${param.id}-textfield`}
          fullWidth
          // label={key}
          value={param.value}
          size="small"
          inputProps={{ type: 'number' }}
          onChange={(e) => handleChange(param.id, parseInt(e.target.value))}
          disabled={disabled}
        />
      );
    case 'input-integer':
      return (
        <Input
          key={`${param.id}-input-integer`}
          fullWidth
          value={param.value}
          size="small"
          inputProps={{ type: 'number' }}
          onChange={(e) => handleChange(param.id, parseInt(e.target.value))}
          disabled={disabled}
        />
      );
    case 'input-number':
      return (
        <Input
          key={`${param.id}-input-float`}
          fullWidth
          value={param.value}
          size="small"
          inputProps={{ type: 'number', step: '0.1' }}
          onChange={(e) => handleChange(param.id, parseFloat(e.target.value))}
          disabled={disabled}
        />
      );
    case 'muxv2': {
      const options = node?.id && listSelectorOptions ? listSelectorOptions(node.id) : [];

      if (isFplMigrationEnabled) {
        return (
          <div {...stylex.props(styles.muxControl)}>
            <Dropdown
              value={param.value}
              onChange={(option) => handleChange(param.id, option.value)}
              options={options.map((option: any, index: number) => ({
                id: String(index),
                label: option,
                value: index,
              }))}
              size="small"
              width="100%"
              placement="bottom-start"
              offset={4}
              disabled={disabled}
              matchTriggerWidth
            />
          </div>
        );
      }

      return (
        <FormControl fullWidth sx={{ mt: 1 }}>
          <Dropdown
            value={param.value}
            onChange={(option) => handleChange(param.id, option.value)}
            options={options.map((option: any, index: number) => ({
              id: String(index),
              label: option,
              value: index,
            }))}
            size="small"
            width="100%"
            placement="bottom-start"
            offset={4}
            disabled={disabled}
            matchTriggerWidth
          />
        </FormControl>
      );
    }
    case 'boolean':
      if (isFplMigrationEnabled) {
        return (
          <div key={`${param.id}-boolean`} {...stylex.props(styles.booleanControl)}>
            <AppCheckbox
              inputProps={{ 'aria-label': translate(I18N_KEYS.GENERAL.CHECKBOX) }}
              checked={param.value as boolean}
              onChange={(e) => handleChange(param.id, e.target.checked)}
              disabled={disabled}
            />
          </div>
        );
      }

      return (
        <FlexCenVer
          key={`${param.id}-boolean`}
          sx={{
            position: 'relative',
            mt: 1,
          }}
        >
          <FormControl>
            <AppCheckbox
              inputProps={{ 'aria-label': translate(I18N_KEYS.GENERAL.CHECKBOX) }}
              checked={param.value as boolean}
              onChange={(e) => handleChange(param.id, e.target.checked)}
              disabled={disabled}
            />
          </FormControl>
        </FlexCenVer>
      );
    case 'seed':
      if (isFplMigrationEnabled) {
        return (
          <div {...stylex.props(styles.seedControl)}>
            <AppCheckbox
              inputProps={{ 'aria-label': translate(I18N_KEYS.GENERAL.CHECKBOX) }}
              checked={(param.value as SeedValue)?.isRandom}
              onChange={(e) =>
                handleChange(param.id, {
                  isRandom: e.target.checked,
                  seed: (param.value as SeedValue)?.seed,
                })
              }
              disabled={disabled}
            />
            <TextStdRg color={token.text}>Random</TextStdRg>

            <div {...stylex.props(styles.seedInput)}>
              <LineInput
                fullWidth
                value={(param.value as SeedValue)?.seed}
                inputProps={{ type: 'number' }}
                size="small"
                onChange={(e) =>
                  handleChange(param.id, {
                    isRandom: (param.value as SeedValue)?.isRandom,
                    seed: e.target.value,
                  })
                }
                disabled={(param.value as SeedValue)?.isRandom}
              />
            </div>
          </div>
        );
      }

      return (
        <FlexCenVer sx={{ gap: 1, mt: 1 }}>
          <FormControl>
            <AppCheckbox
              inputProps={{ 'aria-label': translate(I18N_KEYS.GENERAL.CHECKBOX) }}
              checked={(param.value as SeedValue)?.isRandom}
              onChange={(e) =>
                handleChange(param.id, {
                  isRandom: e.target.checked,
                  seed: (param.value as SeedValue)?.seed,
                })
              }
              disabled={disabled}
            />
          </FormControl>
          <TextStdRg color={token.text}>Random</TextStdRg>

          <Box sx={{ width: '130px', fontSize: '0.75rem' }}>
            <LineInput
              fullWidth
              value={(param.value as SeedValue)?.seed}
              inputProps={{ type: 'number' }}
              size="small"
              onChange={(e) =>
                handleChange(param.id, { isRandom: (param.value as SeedValue)?.isRandom, seed: e.target.value })
              }
              disabled={(param.value as SeedValue)?.isRandom}
            />
          </Box>
        </FlexCenVer>
      );
    case 'array':
      return (
        <>
          <TextArea
            fullWidth
            id={param.id}
            value={Array.isArray(param.value) ? param.value.join(', ') : param.value || ''}
            rows={3}
            onChange={(e) => {
              // Just pass the raw text value
              handleChange(param.id, e.target.value);
            }}
            onBlur={(e) => {
              // Only split into array when the field loses focus
              const newValue = e.target.value
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean);
              handleChange(param.id, newValue);
            }}
            size="small"
            disabled={disabled}
            inputProps={{ style: { resize: 'none' } }}
          />
          <TextSmRg>Use comma for multiple entries </TextSmRg>
        </>
      );
    case 'text':
      return <TextFieldWithCursor id={param.id} value={param.value} property={param} handleChange={handleChange} />;

    case 'color_palette':
      return (
        <DesignAppColorPaletteInput
          id={param.id}
          value={param.value as { colors: WasmColor[] } | undefined}
          onChange={handleChange}
          disabled={disabled}
        />
      );

    case 'multilora':
      return (
        <>
          {isLoading ? (
            <>
              <Skeleton width="100%" height={48} />
              <FlexCenVer sx={{ gap: 1 }}>
                <Skeleton width="20%" height={18} />
                <Skeleton width="60%" height={18} />
                <Skeleton width="20%" height={18} />
              </FlexCenVer>
            </>
          ) : (
            <>
              <LoraSelect
                node={node}
                param={param}
                handleChange={handleChange}
                validationProps={validationProps}
                disabled={disabled}
              />
              <MultiLoraWeightField
                disabled={disabled}
                label={translate(I18N_KEYS.GENERAL.LORA_WEIGHT)}
                onChange={(weight) => {
                  handleChange(node?.id || param.id, {
                    weight,
                    selectedLora: (param.value as MultiLoraValue).selectedLora,
                  });
                }}
                value={(param.value as MultiLoraValue).weight}
              />
            </>
          )}
        </>
      );
    case 'import':
      return (
        <UploadFile
          id={param.id}
          value={param.value}
          onUpload={handleChange}
          isLoading={false}
          setIsUploading={() => {}}
          isGuest={false}
        />
      );
    case NodeType.Prompt:
      return (
        <TextArea
          placeholder={translate(I18N_KEYS.SHARE_WORKFLOW_MODAL.INPUTS.PROMPT_PLACEHOLDER)}
          aria-label={translate(I18N_KEYS.SHARE_WORKFLOW_MODAL.INPUTS.PROMPT_PLACEHOLDER)}
          fullWidth
          value={(param.value as PromptValue)?.prompt}
          onChange={(e) =>
            handleChange(param.id, {
              prompt: e.target.value,
            })
          }
          minRows={4}
          maxRows={20}
          sx={{
            background: `${color.Black100}`,
          }}
          disabled={disabled}
        />
      );
    default:
      return null;
  }
};

/**
 * Preview only offers its legacy editing (header, reorder, expose) to an editor while the Tool tab is
 * off. With the Tool tab on, the tool reads `recipe.toolConfig`, which only the Tool tab writes, so
 * those edits land in `designAppMetadata` and vanish on reload.
 */
export function getDesignAppMode(isEditor: boolean, isToolTabEnabled: boolean): DesignAppMode {
  return isEditor && !isToolTabEnabled ? DesignAppMode.Editing : DesignAppMode.Running;
}

/**
 * The tool's output nodes: the config's declared nodes still on the graph, else the legacy
 * `workflow_output` nodes.
 *
 * Config-first, on the same rule as `getRecipeInspect` in core. A config declares the producing
 * nodes themselves, where a `workflow_output` node is a sink wired to them. An **absent** config
 * falls back to the legacy node; a config whose `outputs` is **empty** does not, because that is a
 * builder who removed their output and falling back would resurrect the legacy one. A stored config
 * with no `outputs` array counts as absent, as core's `parseToolConfig` treats it.
 */
export function getToolOutputNodeIds(nodes: Node[], toolConfig?: ToolConfig | null): string[] {
  if (!Array.isArray(toolConfig?.outputs)) {
    return nodes.filter((node) => node.type === NodeType.WorkflowOutput).map((node) => node.id);
  }
  const nodeIds = new Set(nodes.map((node) => node.id));
  return toolConfig.outputs.map((output) => output?.nodeId).filter((nodeId) => nodeIds.has(nodeId));
}

/** The nodes the tool is built from: its outputs plus everything upstream of them, walking edges in reverse. */
export function getOutputConnectedNodeIds(nodes: Node[], edges: Edge[], toolConfig?: ToolConfig | null): Set<string> {
  return getUpstreamNodes(edges, getToolOutputNodeIds(nodes, toolConfig));
}

/**
 * Returns leaf nodes (no incoming edges) among {@link getOutputConnectedNodeIds}. Under a tool config, a
 * List selector fed by static sources is the input in their place, as the Tool tab and core read it.
 */
export function getWorkflowInputNodes(nodes: Node[], edges: Edge[], toolConfig?: ToolConfig | null): Node[] {
  if (Array.isArray(toolConfig?.outputs)) {
    const { leafIds } = getToolInputLeaves(
      nodes,
      edges,
      getToolOutputNodeIds(nodes, toolConfig),
      toolConfig.lockedAtConversion ?? null,
    );
    return nodes.filter((node) => leafIds.has(node.id));
  }
  const connectedIds = getOutputConnectedNodeIds(nodes, edges, toolConfig);
  const targetIds = new Set(edges.map((e) => e.target));

  return nodes.filter((node) => connectedIds.has(node.id) && !targetIds.has(node.id));
}

/** The List selectors a tool config reads as fed by static sources; Preview hides them until exposed, as core does. */
export function getFedSelectorIds(nodes: Node[], edges: Edge[], toolConfig?: ToolConfig | null): Set<string> {
  if (!Array.isArray(toolConfig?.outputs)) {
    return new Set();
  }
  return getToolInputLeaves(
    nodes,
    edges,
    getToolOutputNodeIds(nodes, toolConfig),
    toolConfig.lockedAtConversion ?? null,
  ).fedSelectorIds;
}

function extractTextValue(value: DesignAppInput['value'] | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('prompt' in value && typeof value.prompt === 'string') return value.prompt;
    if ('string' in value && typeof value.string === 'string') return value.string;
  }
  return '';
}

/**
 * Builds synthetic inputs for delimiter-mode iterators that have incoming edges.
 * These iterators aren't shown as DesignApp inputs (their values come from the
 * graph), but the server needs the resolved options for cost calculation. We
 * split the upstream input value by the iterator's delimiter on the client side
 * since the WASM graph isn't synced with DesignApp input changes.
 */
export function getDelimiterIteratorInputs(
  nodes: Node[],
  edges: Edge[],
  allInputs: DesignAppInput[],
  inputNodeIds: Set<string>,
): { nodeId: string; input: { options: string[] }; disabled: boolean; name: string | undefined }[] {
  return nodes
    .filter(
      (n) =>
        n.type === NodeType.MuxV2 &&
        isIterator(n.data as BaseNodeData & { isIterator?: boolean }) &&
        (n.data as { delimiter?: string }).delimiter &&
        edges.some((e) => e.target === n.id) &&
        !inputNodeIds.has(n.id),
    )
    .map((n) => {
      const delimiter = (n.data as { delimiter?: string }).delimiter!;
      const sourceEdge = edges.find((e) => e.target === n.id);
      const sourceInput = allInputs.find((i) => i.id === sourceEdge?.source);
      const options = extractTextValue(sourceInput?.value)
        .split(delimiter)
        .map((v) => v.trim())
        .filter((v) => v);
      return { nodeId: n.id, input: { options }, disabled: false, name: n.data.name };
    });
}
